import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { setUserData } from '../../../stores/UserStore'
import authService from '../../../services/authService'
import { fileService } from '../../../services/back4app'
import { mergeUserDataWithProfile, readParseGoogleLinked } from '../../../account'
import { getDisplayName } from '../../../utils/personName'
import AvatarUploadField, { type AvatarChange } from '../../profile/AvatarUploadField'
import CharacterPicker from '../../profile/CharacterPicker'
import {
  MAX_DISPLAY_NAME_LENGTH,
  MAX_PROFILE_NAME_LENGTH as MAX_NAME_LENGTH,
  PROFILE_PHOTO_EXTENSIONS,
} from '../../../constants/profile'


/**
 * "Your Sevv self" (SC-438): photo, character, first and last name, and an optional display name
 * that is shown instead of the full name. Names already known (sign-up, Google, the invite CSV)
 * are filled in. Saving the profile finishes the step.
 *
 * A picked photo is only previewed locally; it is uploaded when the person continues, so photos
 * they replace or abandon never reach storage.
 */
export default function SevvSelfStep({ busy, onSaved }: { busy: boolean; onSaved: () => void }) {
  const dispatch = useAppDispatch()
  const userData = useAppSelector((s) => s.user.userData)
  const [firstName, setFirstName] = useState(userData?.firstName || '')
  const [lastName, setLastName] = useState(userData?.lastName || '')
  const [displayName, setDisplayName] = useState(userData?.displayName || '')
  const [character, setCharacter] = useState(userData?.gameCharacter || 'adam')
  /** What the preview shows: a stored URL, or the local preview of `pendingPhoto`. */
  const [photo, setPhoto] = useState<string | null>(userData?.customAvatarUrl || null)
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null)
  const previewUrl = useRef<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const discardPreview = () => {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current)
    previewUrl.current = null
  }

  useEffect(() => discardPreview, [])

  if (!userData) return null

  const firstMissing = !firstName.trim()
  const lastMissing = !lastName.trim()
  const preview = getDisplayName({
    displayName,
    firstName,
    lastName,
    username: userData.username,
  })

  const handlePhotoChange = (change: AvatarChange) => {
    discardPreview()
    if (change.source === 'file') {
      previewUrl.current = URL.createObjectURL(change.file)
      setPendingPhoto(change.file)
      setPhoto(previewUrl.current)
      return
    }
    setPendingPhoto(null)
    setPhoto(change.source === 'google' ? change.url : null)
  }

  /** The stored photo URL to save: the picked file uploaded now, or the photo already chosen. */
  const uploadPendingPhoto = async (): Promise<string | null> => {
    if (!pendingPhoto) return photo
    setUploading(true)
    try {
      // `<userId>-` prefix: the cloud only deletes a replaced photo whose name carries the owner's
      // id (SC-270).
      const random = Math.random().toString(36).substring(2, 15)
      const fileName = `${userData.id}-${random}.${PROFILE_PHOTO_EXTENSIONS[pendingPhoto.type]}`
      const { data, error: uploadError } = await fileService.uploadFile(pendingPhoto, fileName)
      if (uploadError || !data?.publicUrl) {
        throw new Error(uploadError?.message || 'Your photo could not be uploaded. Try again.')
      }
      // Kept as the chosen photo, so a retry after a failed profile save doesn't upload it again.
      discardPreview()
      setPendingPhoto(null)
      setPhoto(data.publicUrl)
      return data.publicUrl
    } finally {
      setUploading(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitted(true)
    if (firstMissing || lastMissing) return

    setError('')
    let photoUrl: string | null
    try {
      photoUrl = await uploadPendingPhoto()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Your photo could not be uploaded. Try again.')
      return
    }

    setSaving(true)
    const { data, error: saveError } = await authService.updateProfile(userData.id, {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      displayName: displayName.trim(),
      gameCharacter: character,
      customAvatarUrl: photoUrl || '',
      avatar: photoUrl ? 'custom' : userData.avatar && userData.avatar !== 'custom' ? userData.avatar : 'beam',
      profileCompleted: true,
    })
    setSaving(false)
    if (saveError || !data) {
      setError(saveError || 'Your profile could not be saved. Try again.')
      return
    }
    dispatch(
      setUserData(mergeUserDataWithProfile(userData, data, { googleLinked: readParseGoogleLinked() }))
    )
    onSaved()
  }

  const sectionLabel = (text: string) => (
    <Typography variant="overline" component="h2" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1.5 }}>
      {text}
    </Typography>
  )

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h3" component="h1" fontWeight={800}>
            Meet your Sevv self
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
            Your <b>photo</b> shows when your camera is off and in chat. Your <b>character</b> is you walking
            around the office.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 3, md: 4 },
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 5fr) minmax(0, 7fr)' },
            alignItems: 'start',
          }}
        >
          <Box sx={{ textAlign: 'center' }} data-testid="sevv-self-photo-column">
            {sectionLabel('Profile photo')}
            <AvatarUploadField
              userId={userData.id}
              value={photo}
              onChange={handlePhotoChange}
              canUseGooglePhoto={Boolean(userData.googleLinked ?? readParseGoogleLinked())}
              disabled={uploading || saving}
            />
          </Box>

          <Stack spacing={3} data-testid="sevv-self-details-column">
            <Box>
              {sectionLabel('Your character')}
              <CharacterPicker value={character} onChange={setCharacter} />
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                required
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                inputProps={{ maxLength: MAX_NAME_LENGTH }}
                error={submitted && firstMissing}
                helperText={submitted && firstMissing ? 'Enter your first name.' : ' '}
              />
              <TextField
                fullWidth
                required
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                inputProps={{ maxLength: MAX_NAME_LENGTH }}
                error={submitted && lastMissing}
                helperText={submitted && lastMissing ? 'Enter your last name.' : ' '}
              />
            </Stack>

            <TextField
              fullWidth
              label="Display name (optional)"
              placeholder={firstName.trim() ? `e.g. ${firstName.trim()}` : undefined}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              inputProps={{ maxLength: MAX_DISPLAY_NAME_LENGTH }}
              helperText={`Shown instead of your full name. ${displayName.length}/${MAX_DISPLAY_NAME_LENGTH}`}
            />

            <Typography variant="body2" color="text.secondary" data-testid="sevv-self-name-preview">
              Others will see you as <b>{preview}</b>
            </Typography>
          </Stack>
        </Box>

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" color="text.secondary">
            You can change these anytime in your profile.
          </Typography>
          <Button type="submit" variant="contained" size="large" disabled={busy || saving || uploading}>
            {uploading ? 'Uploading photo…' : saving ? 'Saving…' : 'Continue →'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}
